import React from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  MapPin,
  Coffee,
  Building2,
  Footprints,
  Sparkles,
  Hotel,
  Utensils,
  Beer,
  Landmark,
  TreePine,
  BookOpen,
  ShoppingBag,
  Plane,
  Mountain,
  Car,
  PawPrint,
} from 'lucide-react';
import ItineraryMap, { buildMapLocations } from './ItineraryMap';
import { dublinCoordinates, dublinCenter } from './coordinates/dublin';
import { CoeliacRef, CoeliacAward, CoeliacRefTag, CoeliacAwardTag } from './CoeliacTags';

interface Activity {
  icon: React.ReactNode;
  title: string;
  description: string;
  time?: string;
  image?: string;
  location?: string;
  muted?: boolean;
  jamieRec?: boolean;
  coeliacRefs?: CoeliacRef[];
  coeliacSummary?: string;
  coeliacAward?: CoeliacAward;
}

interface Day {
  date: string;
  dayNumber: number;
  title: string;
  subtitle: string;
  theme: 'dublin-arrival' | 'dublin-explore' | 'dublin-event' | 'dublin-relax' | 'dublin-depart';
  activities: Activity[];
  highlight?: boolean;
}

const dublinItinerary: Day[] = [
  {
    date: 'Wednesday, April 29',
    dayNumber: 1,
    title: 'Dublin Arrival',
    subtitle: 'Late flight in from London — check in, quick dinner, early bed',
    theme: 'dublin-arrival',
    activities: [
      {
        icon: <Plane size={24} />,
        title: 'Arrive Dublin Airport',
        description:
          "Flight lands around 19:20 after the 18:05 from Heathrow. Airlink 757 express to city centre is €7 and runs every 15–20 min; taxi / Bolt is €25–35 and takes ~30 min. You'll be at Zanzibar Locke by 20:30.",
        location: 'Dublin Airport',
        time: '7:20 PM',
        image: '/images/dublin/dublin-skyline.jpg',
        muted: true,
      },
      {
        icon: <Hotel size={24} />,
        title: "Zanzibar Locke, Ha'Penny Bridge",
        description:
          "Check in to Zanzibar Locke on Ormond Quay Lower — a design-led aparthotel in four restored Georgian townhouses right at Ha'Penny Bridge. Studio apartment with full kitchen, living space, and in-unit laundry. Central but quieter than the Temple Bar hotels. ~5 min walk to Temple Bar, ~13 min to Trinity.",
        location: "Ormond Quay Lower, Ha'Penny Bridge",
        time: '8:30 PM',
        image: '/images/dublin/hoxton-hotel.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Light dinner nearby',
        description:
          'Keep it simple — grab something from nearby Temple Bar or order in. Bambino on Aungier Street (NY-style pizza slices, ~10 min walk) is good late-night food. Early night pays off: tomorrow is a full Dublin day.',
        time: '9:00 PM',
        image: '/images/dublin/dublin-restaurant.jpg',
        muted: true,
      },
    ],
  },
  {
    date: 'Thursday, April 30',
    dayNumber: 2,
    title: 'Dublin Classics',
    subtitle: 'Trinity, Book of Kells, galleries, pubs, Glas',
    theme: 'dublin-explore',
    activities: [
      {
        icon: <Building2 size={24} />,
        title: 'National Gallery of Ireland',
        description:
          "Free entry. Home to an exceptional collection including Caravaggio's 'The Taking of Christ', the Jack B. Yeats rooms, and Frederic William Burton's 'The Meeting on the Turret Stairs' — one of Ireland's most beloved paintings. Check viewing hours for the Burton as it has limited access.",
        location: 'Merrion Square West',
        time: '10:00 AM',
        image: '/images/dublin/national-gallery.jpg',
        jamieRec: true,
      },
      {
        icon: <TreePine size={24} />,
        title: "St Stephen's Green",
        description:
          'A short walk west from the National Gallery. This 9-hectare Victorian-era park dates from 1880 — formal lake, bandstand, a memorial garden to the 1916 Rising, and a playground. Gorgeous in May when the flower beds are in full bloom.',
        location: "St Stephen's Green",
        time: '11:30 AM',
        image: '/images/dublin/st-stephens-green.jpg',
        jamieRec: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Milano — Dawson Street',
        description:
          "Reliable lunch stop on Dawson Street between St Stephen's Green and Trinity. Italian-American chain (Pizza Express in the UK) with a long-standing GF menu — GF pizza bases, GF pasta, and clearly marked allergen choices. Staff are trained on cross-contamination, though it isn't a dedicated GF kitchen. Good for a quick sit-down before the Book of Kells slot.",
        location: 'Dawson Street',
        time: '12:30 PM',
        image: '/images/dublin/dublin-restaurant.jpg',
        coeliacSummary:
          'GF pizza base and GF pasta available across the menu. Staff trained on allergens but not a dedicated GF kitchen — flag coeliac when ordering.',
        coeliacRefs: [
          {
            title: 'Milano allergens & dietary',
            url: 'https://www.milano.ie/allergens-dietary/',
            summary: 'Official allergen info — GF base and GF pasta options available.',
          },
          {
            title: 'FindMeGlutenFree — Milano Dawson Street',
            url: 'https://www.findmeglutenfree.com/ie/dublin/milano-dawson-street',
            summary: 'GF pizza and pasta. Coeliac diners report careful handling though shared kitchen.',
          },
        ],
      },
      {
        icon: <BookOpen size={24} />,
        title: 'The Book of Kells — Trinity College',
        description:
          "The illuminated manuscript dates from around 800 AD; the Long Room library holds 65 metres of floor-to-ceiling old books. 1:30 PM tour booked. Trinity's cobblestone grounds, the Campanile bell tower, and the cricket pitch are worth wandering after.",
        location: 'Trinity College Dublin',
        time: '1:30 PM',
        image: '/images/dublin/trinity-college.jpg',
        jamieRec: true,
      },
      {
        icon: <BookOpen size={24} />,
        title: 'Chester Beatty Library',
        description:
          "Inside Dublin Castle grounds. Lonely Planet called it not just the best museum in Dublin, but one of the best in Europe. Over 20,000 manuscripts, rare books, and artefacts spanning Egypt to Japan from 2700 BC. European Museum of the Year 2002. Completely free. The Silk Road Café does excellent Middle Eastern food and is almost entirely GF — one of Dublin's best coeliac-friendly spots.",
        location: 'Dublin Castle grounds',
        time: '3:00 PM',
        image: '/images/dublin/chester-beatty.jpg',
        coeliacSummary:
          'Silk Road Café is almost entirely GF with clearly marked dishes. Middle Eastern cuisine using naturally GF ingredients. Highly rated by coeliac diners.',
        coeliacRefs: [
          {
            title: 'Silk Road Cafe official',
            url: 'https://silkroadcafe.ie/',
            summary: 'Almost entirely GF menu. Middle Eastern cuisine, clearly marked GF dishes.',
          },
          {
            title: 'FindMeGlutenFree',
            url: 'https://www.findmeglutenfree.com/biz/silk-road-cafe/6516420570120192',
            summary: 'Highly rated by coeliacs. GF afternoon tea available Thu-Sat.',
          },
        ],
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Oxfam Books, Parliament Street',
        description:
          'Brief charity-bookshop stop on Parliament Street just after the Castle. Properly curated secondhand stock — fiction, history, art, and Irish-interest sections — in a tiny shopfront. A 15-minute browse on the way to House of Read a few doors away.',
        location: 'Parliament Street',
        time: '4:00 PM',
        muted: true,
      },
      {
        icon: <Coffee size={24} />,
        title: "House of Read — Dublin's oldest shop",
        description:
          "Tea, coffee, and macaroons at Dublin's oldest shop on Parliament Street — established 1670, still trading from the original premises after 350+ years. Built as a cutler's, expanded onto Parliament Street in 1760, and restored in 2017 (winner of the international Diaphoros Award for best conservation). The café serves home-brewed coffee, tea, hot chocolate, plus house chocolates, macaroons, eclairs, and pastries.",
        location: 'Parliament Street',
        time: '4:30 PM',
        muted: true,
      },
      {
        icon: <Beer size={24} />,
        title: "Kehoe's Pub",
        description:
          "Over 200 years old and one of Dublin's finest traditional pubs. Consistently rated as having one of the best pints of Guinness in the city. The upstairs snug is worth finding — small and wood-panelled.",
        location: 'South Anne Street',
        time: '5:30 PM',
        image: '/images/dublin/kehoes-pub.jpg',
        jamieRec: true,
      },
      {
        icon: <Beer size={24} />,
        title: "Grogan's",
        description:
          "A Dublin institution since 1899 — no music, no TV, just great pints and conversation. A haunt for writers and artists; the walls are lined with paintings by regular customers. Easy 5-minute walk from Kehoe's.",
        location: 'South William Street',
        time: '6:45 PM',
        image: '/images/dublin/grogans-pub.jpg',
        jamieRec: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Glas Restaurant',
        description:
          "Ireland's only 100% gluten-free restaurant, on Chatham Street just off Grafton Street. Vegetarian and mostly vegan, with no wheat, barley, or rye in the kitchen — zero cross-contamination risk. Chef Malte Half's seasonal menu is creative and beautifully presented. Won the Coeliac Society of Ireland's Best Practice in Gluten Free Catering award. Recognised by the Michelin Guide. Book ahead.",
        location: 'Chatham Street',
        time: '8:00 PM',
        image: '/images/dublin/dublin-restaurant.jpg',
        coeliacSummary:
          "Ireland's only 100% GF restaurant — no gluten handled in the kitchen. Winner of Coeliac Society of Ireland's Best Practice in GF Catering award. Michelin-recognised. Zero cross-contamination risk.",
        coeliacRefs: [
          {
            title: 'Glas Restaurant',
            url: 'https://glasrestaurant.ie/menus/',
            summary: 'Official site. 100% GF menu — no gluten handled in kitchen.',
          },
          {
            title: 'FindMeGlutenFree',
            url: 'https://www.findmeglutenfree.com/biz/glas-restaurant/6460898180464640',
            summary: 'Listed as dedicated GF. Coeliacs report total peace of mind.',
          },
          {
            title: 'Hotel & Restaurant Times',
            url: 'https://www.hotelandrestauranttimes.ie/2024/10/10/glas-restaurant-irelands-first-fully-gluten-free-restaurant/',
            summary: "Ireland's first fully GF restaurant. Coeliac Society award winner.",
          },
        ],
        coeliacAward: { body: 'Coeliac Society IE', award: 'Best GF Catering', url: 'https://coeliac.ie/' },
      },
    ],
  },
  {
    date: 'Friday, May 1',
    dayNumber: 3,
    title: 'Light Day — Coffee, Customer Lunch, Grafton',
    subtitle: "Kaph in the morning, customer lunch at The Ivy, Matt to Grogan's — bigger sights pushed to Saturday",
    theme: 'dublin-explore',
    activities: [
      {
        icon: <Coffee size={24} />,
        title: 'Kaph Coffee',
        description:
          'Specialty coffee roasters on Drury Street since 2013. Single-origin pourovers and flat whites, house-baked cakes with GF options. Quick stop together before the day splits.',
        location: 'Drury Street',
        time: '8:00 AM',
        image: '/images/dublin/dublin-coffee.jpg',
        jamieRec: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Matt — customer lunch at The Ivy Dublin',
        description:
          'Customer-hosted lunch at The Ivy on Dawson Street, 12–3 PM. Brasserie-style menu with allergen filters via QR code and lots of marked GF options across mains and desserts (weekend brunch even has GF pancakes). Knowledgeable allergen-aware staff. Not a dedicated GF kitchen and no separate GF fryer — flag coeliac at ordering and skip fried items to be safe.',
        location: 'Dawson Street',
        time: '12:00 PM',
        image: '/images/dublin/dublin-restaurant.jpg',
        muted: true,
        coeliacSummary:
          "Allergen-filtered menu via QR code with multiple GF mains and desserts. Staff knowledgeable about cross-contamination but kitchen is shared and there's no dedicated GF fryer. Flag coeliac when ordering and avoid fried dishes.",
        coeliacRefs: [
          {
            title: 'FindMeGlutenFree — The Ivy Dublin',
            url: 'https://www.findmeglutenfree.com/biz/the-ivy/6429144991268864',
            summary:
              'Allergen-filtered menu, GF options across the menu, allergen-aware staff. Not a dedicated GF kitchen; no GF fryer.',
          },
          {
            title: 'The Ivy Dublin — menus',
            url: 'https://theivydublin.com/menus/',
            summary: 'Official menu site for the Dawson Street location.',
          },
        ],
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Megan — Moore Street Market',
        description:
          "Dublin's oldest street market, ~10 min walk north of the hotel via O'Connell Bridge and Henry Street. Open Mon–Sat. Fruit, veg, fish, and African/Asian grocers — characters, hand-written signs, no tourist polish. Properly old-school Dublin street trade.",
        location: 'Moore Street',
        time: '12:00 PM',
        muted: true,
      },
      {
        icon: <ShoppingBag size={24} />,
        title: "Megan — George's Street Arcade",
        description:
          "Covered Victorian indoor market on South Great George's Street (~10 min walk south from Moore Street, crossing the Liffey at O'Connell Bridge). Vintage jewellery, secondhand books, records, palmistry stalls, vintage clothes, and small food kiosks. Easily an hour of poking around.",
        location: "South Great George's Street",
        time: '12:45 PM',
        muted: true,
      },
      {
        icon: <ShoppingBag size={24} />,
        title: "Megan — Lucy's Lounge",
        description:
          "Basement vintage institution in Temple Bar — Fownes Street, ~5 min walk from George's Street Arcade. Pink staircase landmark, decades of curated thrift across thousands of pieces. Cash often preferred. Properly quirky.",
        location: 'Temple Bar',
        time: '1:30 PM',
        muted: true,
      },
      {
        icon: <ShoppingBag size={24} />,
        title: "Megan — Vincent's & charity-shop crawl",
        description:
          "Wicklow Street and Aungier Street have several charity shops within a few minutes' walk of each other — Vincent's (St Vincent de Paul), Enable Ireland, NCBI Books. Stock turns over fast and prices are low. Easy 20-minute browse on the way to Nine Crows.",
        location: 'Wicklow Street',
        time: '2:00 PM',
        muted: true,
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Megan — Nine Crows',
        description:
          "Aungier Street vintage shop with a strong 90s/Y2K curation — denim, band tees, sportswear, leather. Independent and unfussy. Sister store on Capel Street if there's still time. Wraps up close to the hotel for the 3 PM regroup with Matt.",
        location: 'Aungier Street',
        time: '2:30 PM',
        muted: true,
      },
      {
        icon: <Beer size={24} />,
        title: "Matt — Grogan's",
        description:
          "Skipped Brown Thomas and went for an early afternoon pint at Grogan's instead — South William Street, a Dublin institution since 1899. No music, no TV, just great pints, toasted sandwiches, and conversation. The walls are lined with paintings by regular customers. Easy walk back to the hotel afterwards.",
        location: 'South William Street',
        time: '3:30 PM',
        image: '/images/dublin/grogans-pub.jpg',
        muted: true,
        jamieRec: true,
      },
    ],
  },
  {
    date: 'Saturday, May 2',
    dayNumber: 4,
    title: 'Big Day — Phoenix, Wolfhound, Liberties, National Museum',
    subtitle:
      "Storehouse fell through (couldn't book) — easier morning, then Wolfhound, Two Pups lunch + Megan's wander, National Museum at 4 PM",
    theme: 'dublin-relax',
    highlight: true,
    activities: [
      {
        icon: <Footprints size={24} />,
        title: 'Phoenix Park',
        description:
          "Early start to fit the day. One of the largest enclosed city parks in Europe — 707 hectares of mostly flat, tree-lined paths. Wild fallow deer roam freely near the Papal Cross. Chesterfield Avenue runs ~4km end-to-end: wide and flat — good for a gentle walk. Also home to Dublin Zoo and Áras an Uachtaráin (President's residence). Aim for 7–8:30 AM, then taxi back into the city for a proper coeliac-safe breakfast at Cornucopia.",
        location: 'Phoenix Park',
        time: '7:00 AM',
        image: '/images/dublin/phoenix-park.jpg',
        jamieRec: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Cornucopia — Breakfast on Wicklow Street',
        description:
          "Long-running vegetarian/vegan wholefoods restaurant on Wicklow Street, just off Grafton Street and a 5-min walk from St Stephen's Green where the Wolfhound tour starts. Breakfast is served 8:30 AM – 12:00 noon. Most of the menu is gluten-free or coeliac-friendly and every GF dish is marked \"GF\" on the daily blackboard. Reliable GF breakfast picks: almond/pumpkin-seed/banana granola, overnight oats, scrambled tofu on GF bread, smoked baked pinto beans on GF bread. Coffee is good — covers the morning caffeine stop too. Counter service, no booking. Flag coeliac when ordering — staff are well-trained and will check with the kitchen if unsure. ~15 min taxi from Phoenix Park; easy 5-min walk over to St Stephen's Green for the 12:30 Wolfhound when you're done.",
        location: 'Wicklow Street',
        time: '8:45 AM',
        image: '/images/dublin/dublin-coffee.jpg',
        coeliacSummary:
          'Most of the menu is GF or coeliac-friendly with all GF dishes clearly marked on the daily blackboard. Strong cross-contamination awareness from staff (will consult colleagues if unsure). Shared kitchen rather than a fully dedicated GF kitchen, so flag coeliac at the till when ordering. No formal Coeliac Society of Ireland accreditation but consistently top-rated by Dublin coeliac diners on FindMeGlutenFree.',
        coeliacRefs: [
          {
            title: 'FindMeGlutenFree',
            url: 'https://www.findmeglutenfree.com/biz/cornucopia/5260521406201856',
            summary:
              'Most main-menu items GF/coeliac-friendly. Daily blackboard marks every GF dish. Staff cross-contamination aware and will check with kitchen.',
          },
          {
            title: 'Cornucopia official',
            url: 'https://cornucopia.ie/',
            summary:
              'Menu caters for gluten-free, coeliac, sugar-free and raw diets. All food made fresh daily on premises.',
          },
          {
            title: 'Visit Dublin listing',
            url: 'https://www.visitdublin.com/cornucopia',
            summary:
              'Vegetarian breakfasts served 8:30 AM – 12:00 noon. Saturday hours 8 AM – 8 PM. Address: 19/20 Wicklow Street, Dublin 2.',
          },
        ],
      },
      {
        icon: <PawPrint size={24} />,
        title: "The Wolfhound Experience — St Stephen's Green",
        description:
          "A 1.5-hour walking tour through St Stephen's Green with Wayne and his pack of Irish Wolfhounds — the world's tallest dog breed — often joined by Oscar the Red Setter and Lady MacBeth the Irish Terrier. Equal parts dog-walk and storytelling: the breed's links to Irish mythology, Cú Chulainn, the old hunting packs, and the near-extinction and revival in the 19th century. Expect strangers to stop you every few minutes — the dogs are celebrities. From €64.99pp. **Booked — 12:30 PM slot confirmed.**",
        location: "St Stephen's Green",
        time: '12:30 PM',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Two Pups Cafe — Lunch in the Liberties',
        description:
          'Specialty coffee shop on Francis Street in the Liberties that doubles as a strong brunch/lunch spot — toasties, eggs, salads, baked goods, excellent coffee. Casual counter service, no booking. Easy 10-min stroll from the Wolfhound finish via the south side. Late-lunch slot at 2:30 PM after the dogs.',
        location: 'Francis Street',
        time: '2:30 PM',
        image: '/images/dublin/dublin-coffee.jpg',
      },
      {
        icon: <Building2 size={24} />,
        title: 'National Museum of Ireland — Archaeology',
        description:
          "Replacement for the Guinness Storehouse (couldn't book). The headline National Museum on Kildare Street — free admission. The headline rooms: the bog bodies (Old Croghan Man and Clonycavan Man — Iron Age sacrifices preserved by acid bogs), the Tara Brooch, the Ardagh Chalice, and the prehistoric gold collection (one of Europe's largest). **Closes 5 PM Saturdays — only ~1 hour at this slot, so head straight for the bog bodies and the gold rooms.** ~15 min taxi from Two Pups, then a 5-min walk to The Long Hall.",
        location: 'Kildare Street',
        time: '4:00 PM',
        image: '/images/dublin/national-museum-archaeology.jpg',
      },
      {
        icon: <Beer size={24} />,
        title: 'The Long Hall',
        description:
          "One of Dublin's most beautiful Victorian pubs — dark carved wood, chandeliers, a red lacquered ceiling, ornate mirrors, and brass foot rails. Licensed since 1766, making it one of Dublin's oldest continually operating pubs. The Guinness here is exceptional.",
        location: "South Great George's Street",
        time: '5:30 PM',
        image: '/images/dublin/long-hall-pub.jpg',
        jamieRec: true,
      },
      {
        icon: <Utensils size={24} />,
        title: "Gallagher's Boxty House",
        description:
          '20–21 Temple Bar — the first restaurant in Ireland to specialise in boxty (traditional Irish potato pancakes from the northwest), family-run since 1989. The menu is built around boxty (boiled, baked, dumplings, pancake parcels stuffed with bacon and cabbage, lamb stew, or chicken), traditional Irish stew, beef and Guinness pie, coddle, and seafood. Coeliac-aware with a marked GF menu including GF boxty, GF stew, and GF pies — a rare combination for traditional Irish food. Books up early on Saturday nights.',
        location: 'Temple Bar',
        time: '7:30 PM',
        image: '/images/dublin/dublin-restaurant.jpg',
        coeliacSummary:
          'Marked GF menu including GF boxty pancakes, GF Irish stew, and GF pies — uncommon for traditional Irish food. Allergen-aware staff. Not a dedicated GF kitchen, so flag coeliac when booking and again at the table.',
        coeliacRefs: [
          {
            title: "Gallagher's Boxty House official",
            url: 'https://boxtyhouse.ie/',
            summary: 'Temple Bar restaurant since 1989. Marked coeliac/GF menu with GF boxty, stew, and pies.',
          },
        ],
      },
    ],
  },
  {
    date: 'Sunday, May 3',
    dayNumber: 5,
    title: 'Dublin with Jason and Inês — Walking Tour, Kilmainham, then West to Galway',
    subtitle:
      'Early brunch, free walking tour, EPIC + Brazen Head, Kilmainham Gaol at 4 PM, then late drive to Galway with a 9:30 PM Handsome Burger dinner',
    theme: 'dublin-event',
    activities: [
      {
        icon: <Hotel size={24} />,
        title: 'Checkout — luggage to lobby',
        description:
          'Check out of Zanzibar Locke and leave bags in the lobby/luggage room. Locke offers same-day storage so you can spend the day in Dublin without dragging luggage. Pick up on the way to Kilmainham/airport in the late afternoon.',
        location: "Ormond Quay Lower, Ha'Penny Bridge",
        time: '8:30 AM',
        muted: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Sunday brunch with Jason and Inês — Brother Hubbard',
        description:
          'Capel Street, ~5 min walk north of the hotel. Popular Sunday brunch with a Middle Eastern–leaning menu, excellent coffee, and a marked GF menu (GF bread/buns, soup, sandwiches, eggs benedict, shakshuka). Early slot to be done by 9:45 AM and walk over to Dame Street for the 10 AM tour — get in at opening to beat the queue. **Not a dedicated GF kitchen** — staff are generally careful and respond well when coeliac is flagged, but coeliac reviews are mixed (one report of a GF salad served with gluten bread). Flag coeliac at booking and again at the table when ordering.',
        location: 'Capel Street',
        time: '8:45 AM',
        image: '/images/dublin/dublin-restaurant.jpg',
        coeliacSummary:
          'Marked GF menu with GF bread, buns, soup, sandwiches, and eggs benedict. NOT a dedicated GF kitchen — cross-contamination risk. Mixed coeliac reviews: staff usually careful and responsive when issues are raised, but at least one documented instance of GF dish served with gluten bread. Flag coeliac at booking and at the table.',
        coeliacRefs: [
          {
            title: 'Brother Hubbard official',
            url: 'https://brotherhubbard.ie/',
            summary: 'Café with allergen-marked menu, GF dishes throughout.',
          },
          {
            title: 'FindMeGlutenFree — Brother Hubbard North',
            url: 'https://www.findmeglutenfree.com/biz/brother-hubbard-north/5896604882173952',
            summary: 'Not dedicated GF. Staff usually knowledgeable and accommodating; some mixed coeliac reports.',
          },
          {
            title: 'Dublin Town — Coeliac-friendly restaurants',
            url: 'https://www.dublintown.ie/stories-guides/13-restaurants-that-offer-coeliac-friendly-food/',
            summary: "Lists Brother Hubbard among Dublin's coeliac-friendly cafés.",
          },
        ],
      },
      {
        icon: <Footprints size={24} />,
        title: 'Discover Dublin & Hidden Gems Free Walking Tour',
        description:
          "A 2.5–3 hour tip-based walking tour covering Dublin's headline sights and a few less-trodden corners — Trinity College, Temple Bar, Christ Church, Dublin Castle, and the guide's pick of hidden lanes and stories. Meets near City Hall on Dame Street. Tours run rain or shine; bring a jacket. Tip your guide €10–20pp at the end.",
        location: 'Dame Street',
        time: '10:00 AM',
      },
      {
        icon: <Building2 size={24} />,
        title: 'EPIC The Irish Emigration Museum',
        description:
          "Interactive museum on Custom House Quay (CHQ Building) — voted Europe's leading tourist attraction three years running. 20 themed galleries trace the Irish diaspora through the world: famine emigration, the Irish in America, music, sport, literature, the Kennedys. Brilliant primer for visitors and a great group activity. ~1.5 hours — slot it in straight after the walking tour. Book online to skip the queue. (Christ Church Cathedral is covered by the morning walking tour, so no separate stop needed.)",
        location: 'Custom House Quay',
        time: '1:15 PM',
        image: '/images/dublin/dublin-skyline.jpg',
      },
      {
        icon: <Beer size={24} />,
        title: 'The Brazen Head — quick pint with Jason and Inês',
        description:
          "Lower Bridge Street, ~20 min walk west of EPIC along the Liffey. Established 1198 — Ireland's oldest pub. Stone courtyard, low-beam interior, a working coal fire most days. The Guinness is excellent. Quick pints stop — leave by 3:30 PM to taxi to Kilmainham (~10 min) and arrive for 3:45 PM. Roughly half the food menu is marked GF (Irish stew, seafood chowder, salmon, lemon garlic chicken, mussels, goat cheese & beetroot salad), but it is NOT a dedicated GF kitchen and there is no GF fryer — keep this as a drinks stop only.",
        location: 'Lower Bridge Street',
        time: '3:00 PM',
        image: '/images/dublin/brazen-head-pub.jpg',
        coeliacSummary:
          'About half the menu is GF-marked (stew, chowder, salmon, mussels). Allergens listed on the menu. NOT a dedicated GF kitchen and NO dedicated GF fryer — cross-contamination risk. Mixed coeliac reviews. Safest treated as a drinks stop; if eating, flag firmly and avoid fried items.',
        coeliacRefs: [
          {
            title: 'The Brazen Head — All Day Menu',
            url: 'https://brazenhead.com/all-day-menu/',
            summary: 'Official menu with allergen markings.',
          },
          {
            title: 'FindMeGlutenFree — The Brazen Head',
            url: 'https://www.findmeglutenfree.com/biz/the-brazen-head/6527902187388928',
            summary:
              'About half the menu is GF. No GF fryer. Not a dedicated GF facility — may not be safe for coeliacs.',
          },
        ],
      },
      {
        icon: <Landmark size={24} />,
        title: 'Kilmainham Gaol — Guided Tour',
        description:
          'The guided tour covers the 1916 Rising and the executions that followed in the stone-cold yard — leaders including Pearse, Connolly, and Plunkett were executed here over nine days in May 1916. **Booked for 4:00 PM — arrive by 3:45 PM.** ~15 min taxi from the Brazen Head; allow ~1.5 hr for the tour.',
        location: 'Inchicore Road, Kilmainham',
        time: '4:00 PM (arrive 3:45 PM)',
        image: '/images/dublin/kilmainham-gaol.jpg',
        jamieRec: true,
      },
      {
        icon: <Car size={24} />,
        title: 'Goodbye to Jason and Inês — taxi/Aircoach to Dublin Airport',
        description:
          "Out of Kilmainham around 5:30 PM. Bolt/taxi straight to Dublin Airport (~30 min, ~€35) is the fastest move — collect luggage from Locke on the way (the hotel is roughly en route). Or jump on the 747 Airlink from O'Connell Street if you'd rather (€7, every 15–20 min) — slower but cheaper. Target the rental depot for 6:30 PM pickup.",
        location: 'Dublin Airport',
        time: '5:45 PM',
        muted: true,
      },
    ],
  },
  // Trip continues on the Galway tab (Sun 3 May evening – Tue 5 May)
  // and the Singapore tab (Wed 6 May layover).
];

const AnimatedSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const DublinTab: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <motion.section
        className="hero dublin-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        <div className="hero-background">
          <img src="/images/dublin/dublin-skyline.jpg" alt="Ha'penny Bridge, Dublin" />
          <div className="hero-overlay dublin-overlay" />
        </div>
        <div className="hero-content">
          <motion.div
            className="hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <Calendar size={16} />
            <span>April 29 – May 3, 2026</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Dublin
          </motion.h1>
          <motion.div
            className="hero-details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="hero-detail">
              <MapPin size={20} />
              <span>Zanzibar Locke, Ha&apos;Penny Bridge</span>
            </div>
            <div className="hero-detail">
              <Mountain size={20} />
              <span>5 Days of Pubs, Galleries &amp; Sights</span>
            </div>
          </motion.div>
        </div>
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="scroll-line" />
        </motion.div>
      </motion.section>

      {/* Introduction */}
      <section className="intro-section dublin-intro">
        <AnimatedSection>
          <div className="intro-content">
            <h2>Dublin 2026</h2>
            <p>
              Five days based at Zanzibar Locke on Ormond Quay (Wed 29 Apr – Sun 3 May): late arrival Wednesday, three
              full city days, and a Sunday family day with Jason and Inês. The west- coast road trip continues on the{' '}
              <strong>Galway tab</strong> from Sunday afternoon, and the homeward Singapore layover is on the{' '}
              <strong>Singapore tab</strong>.
            </p>
            <p>
              The plan groups activities by area to minimise backtracking — Trinity / Merrion on Thursday, lighter
              Friday around Grafton Street with Matt at a customer lunch, the Phoenix Park / Storehouse / Wolfhound /
              Kilmainham blockbuster on Saturday, and a relaxed Sunday-morning Liffey loop with the family before the
              Aircoach to the airport.
            </p>
            <p>
              Budget note: Dublin is expensive. Expect €7–8 for a pint, €50+ for a couple of rounds, and three figures
              for a proper dinner out. Many of the major museums and galleries are free.
            </p>
          </div>
        </AnimatedSection>
      </section>

      {/* Itinerary Timeline */}
      <section className="timeline-section">
        <AnimatedSection>
          <h2 className="section-title dublin-section-title">Day by Day</h2>
        </AnimatedSection>

        <div className="timeline">
          {dublinItinerary.map((day, dayIndex) => (
            <AnimatedSection key={dayIndex}>
              <div className={`day-card ${day.theme} ${day.highlight ? 'highlight dublin-highlight' : ''}`}>
                <div className="day-header">
                  <div className="day-number">Day {day.dayNumber}</div>
                  <div className="day-info">
                    <div className="day-date">{day.date}</div>
                    <h3 className="day-title">{day.title}</h3>
                    <p className="day-subtitle">{day.subtitle}</p>
                  </div>
                </div>

                <div className="activities">
                  {day.activities.map((activity, activityIndex) => (
                    <div
                      key={activityIndex}
                      className={`activity ${activity.muted ? 'muted' : ''} ${!activity.image ? 'no-image' : ''}`}
                    >
                      {activity.image && (
                        <div className="activity-image">
                          <img src={activity.image} alt={activity.title} />
                        </div>
                      )}
                      <div className="activity-content">
                        <div className="activity-header">
                          <div className="activity-icon dublin-icon">{activity.icon}</div>
                          <div className="activity-header-text">
                            <h4 className="activity-title">{activity.title}</h4>
                            {activity.time && <div className="activity-time dublin-time">{activity.time}</div>}
                            {activity.jamieRec && <div className="activity-jamie-tag">Jamie&apos;s Pick</div>}
                          </div>
                        </div>
                        {activity.location && (
                          <div className="activity-location">
                            <MapPin size={12} />
                            <span>{activity.location}</span>
                          </div>
                        )}
                        <p className="activity-description">{activity.description}</p>
                        <div className="coeliac-tags-row">
                          {activity.coeliacAward && <CoeliacAwardTag award={activity.coeliacAward} />}
                          {activity.coeliacRefs && activity.coeliacRefs.length > 0 && (
                            <CoeliacRefTag refs={activity.coeliacRefs} summary={activity.coeliacSummary} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Interactive Map */}
      <ItineraryMap
        locations={buildMapLocations(dublinItinerary, dublinCoordinates)}
        center={dublinCenter}
        zoom={14}
        themeColor="#40916c"
        totalDays={5}
      />

      {/* Booking Notes */}
      <section className="intro-section dublin-intro">
        <AnimatedSection>
          <div className="intro-content">
            <h2>Critical Booking Checklist</h2>
            <p>
              <strong>Kilmainham Gaol</strong> — Book online ASAP. Tickets release in batches and sell out. Check their
              website the morning of for day-of releases around 9am.
            </p>
            <p>
              <strong>The Wolfhound Experience</strong> — Pre-book the Saturday 10:30 AM slot at
              thewolfhoundexperience.com/booking. Small group sizes; weekend slots fill up fastest.
            </p>
            <p>
              <strong>Book of Kells</strong> — Pre-book online. Cheaper than walk-up and guarantees entry.
            </p>
            <p>
              <strong>Guinness Storehouse</strong> — Online tickets are discounted. Book a mid-afternoon slot to avoid
              peak crowds.
            </p>
            <p>
              <strong>Jameson Distillery</strong> — Book the flagship tour online, especially for weekend slots.
            </p>
            <p>
              <strong>Comet Restaurant</strong> — Book ahead for Sunday dinner (closed Mon/Tue). Mention GF/coeliac
              needs when booking.
            </p>
            <p>
              <strong>Fallon & Byrne Wine Cellar</strong> — Book for Saturday evening as an alternative dinner. GF menu
              available.
            </p>
            <p>
              <strong>Galway / Singapore</strong> — see the dedicated Galway and Singapore tabs for the road-trip and
              layover booking checklists (car hire, Galway hotels, Cliffs of Moher timed entry, Kai reservation, db
              Bistro reservation, GFML airline meals).
            </p>
          </div>
        </AnimatedSection>
      </section>

      {/* Pub & Restaurant Quick Reference */}
      <section className="intro-section dublin-intro">
        <AnimatedSection>
          <div className="intro-content">
            <h2>Quick Reference</h2>
            <p>
              <strong>Pubs:</strong> Grogan&apos;s (no-frills institution, est. 1899), Kehoe&apos;s (200+ years, best
              Guinness), The Long Hall (Victorian beauty, licensed 1766), Hogans (casual, younger crowd),
              O&apos;Donoghue&apos;s (live trad music since 1934).
            </p>
            <p>
              <strong>Pizza:</strong> Bambino (NY slices, no GF base). <strong>Burrito:</strong> Pablo Picantes (GF
              bowls available, closed Sundays). <strong>Tapas:</strong> The Port House (owner is coeliac, extensive GF
              menu).
            </p>
            <p>
              <strong>Wine bars:</strong> Loose Canon (cheese boards, Drury St), Cellar22 (more intimate).{' '}
              <strong>Coffee:</strong> Kaph (GF bakes, Drury St), Industry & Co (GF options), Indigo & Cloth.
            </p>
            <p>
              <strong>GF standouts:</strong> Silk Road Café in Chester Beatty (almost entirely GF), The Port House
              (owner is coeliac), Fallon & Byrne (separate GF menu), Comet (GF bread, accommodates with notice).
            </p>
            <p>
              <strong>Getting around:</strong> Almost everything is walkable. The Luas tram is useful for Phoenix Park
              or Kilmainham if legs are tired. Taxis and Bolt are readily available.
            </p>
          </div>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <motion.footer
        className="footer dublin-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="footer-content">
          <Sparkles size={32} />
          <h3>See you in Dublin</h3>
          <p>April 29 – May 6, 2026</p>
        </div>
      </motion.footer>
    </>
  );
};

export default DublinTab;
